import React, { useRef, useState } from 'react'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import { LocalizationProvider as TimePickersLocalizationProvider } from '@mui/x-date-pickers'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import DateRangePicker from '@mui/lab/DateRangePicker'
import { MMAlert } from 'components/Alert'
import { addYears } from 'date-fns'
import { MobileTimePicker } from '@mui/x-date-pickers'
import TextField from '@mui/material/TextField'
import { useGetBusiness } from '@marketing-milk/frontend'

type Props = {
  duration: [Date | null, Date | null]
  setDuration: (duration: [Date | null, Date | null]) => void
  disableStart: boolean
  startTime: Date
  setStartTime: (time: Date | null) => void
  endTime: Date
  setEndTime: (time: Date | null) => void
}

export const CampaignDurationField = ({
  duration,
  setDuration,
  disableStart,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
}: Props) => {
  const business = useGetBusiness()
  const inputsRef = useRef<HTMLDivElement>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  return (
    <div className="w-full grid sm:grid-cols-3 gap-4 py-4 items-start">
      <p className="text-sm font-semibold text-mm-neutral-1-700">Duration</p>
      <div className="sm:col-span-2 space-y-3">
        {disableStart && (
          <MMAlert type={'info'}>
            Once a Campaign has started, you cannot change the start date.
          </MMAlert>
        )}

        {/* A popover rather than an always-open calendar: the static two-month picker was the
            tallest thing on this screen by far, and it pushed everything below it off-screen. */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateRangePicker
            startText="Campaign Start"
            endText="Campaign End"
            disablePast
            maxDate={addYears(new Date(), 1)}
            value={duration}
            onChange={setDuration}
            open={pickerOpen}
            onOpen={() => setPickerOpen(true)}
            // Clicking the other field reads as a click away from the calendar, which would
            // shut it just as someone switches from picking the start to picking the end.
            // Focus has already moved by then, so it says where the click landed.
            onClose={() => {
              if (inputsRef.current?.contains(document.activeElement)) return
              setPickerOpen(false)
            }}
            renderInput={(startProps, endProps) => (
              <div ref={inputsRef} className="flex flex-wrap items-center gap-3">
                <TextField {...startProps} size="small" />
                <span className="text-sm text-mm-neutral-1-600">to</span>
                <TextField {...endProps} size="small" />
              </div>
            )}
          />
        </LocalizationProvider>

        <TimePickersLocalizationProvider dateAdapter={AdapterDateFns}>
          <div className="flex flex-wrap items-start gap-3">
            <MobileTimePicker
              label="Start Time"
              value={startTime}
              onChange={setStartTime}
              renderInput={params => (
                <TextField {...params} size="small" helperText={`Timezone: ${business.timezone}`} />
              )}
            />
            <MobileTimePicker
              label="End Time"
              value={endTime}
              onChange={setEndTime}
              renderInput={params => (
                <TextField {...params} size="small" helperText={`Timezone: ${business.timezone}`} />
              )}
            />
          </div>
        </TimePickersLocalizationProvider>
      </div>
    </div>
  )
}
